import React from "react";

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/src/components/ui/alert";
import { Button } from "@/src/components/ui/button";
import { Card, CardContent } from "@/src/components/ui/card";
import { cn } from "@/src/lib/utils";

import LiveConfirmAction from "./LiveConfirmAction";
import {
  formatClock,
  formatReadyTime,
  formatRecordedDuration,
  formatTime,
  getRemainingSeconds,
} from "./liveFormat";
import LivePhaseGauge from "./LivePhaseGauge";
import {
  ARCHIVED_STYLE,
  LIVE_STATE_STYLE,
  TONE_ICON_CLASS,
} from "./liveStateStyle";
import { formatQuotaDuration } from "../../utils/liveQuota";

import type { JSX } from "react";

import type { LiveReplayRange, UseLiveRegieResult } from "./live.types";
import type { LiveQuotaSuggestion } from "../../utils/liveQuota";

interface LiveStatusBarProps {
  regie: UseLiveRegieResult;
  range: LiveReplayRange;
  troubleshootingUrl: string;
}

interface BarTexts {
  title: string;
  subtitle: string;
}

function LiveStatusBar({
  regie,
  range,
  troubleshootingUrl,
}: LiveStatusBarProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { view, settings, isMutating, actions, now, isArchived } = regie;
  const { state, phase } = view;

  const style = isArchived ? ARCHIVED_STYLE : LIVE_STATE_STYLE[state];
  const remainingSeconds = getRemainingSeconds(view.liveEnd, now);
  const remainingMinutes =
    remainingSeconds === null ? undefined : Math.ceil(remainingSeconds / 60);
  const progress =
    remainingSeconds === null || settings.durationSeconds <= 0
      ? undefined
      : Math.min(
          1,
          Math.max(0, 1 - remainingSeconds / settings.durationSeconds),
        );
  const endTime = formatTime(view.liveEnd);
  const isSpinning =
    !isArchived &&
    ["provisioning", "checkingSignal", "publishing", "stopping"].includes(
      state,
    );

  function getTexts(): BarTexts {
    if (isArchived) return { title: t("liveRegie_archived"), subtitle: "" };
    switch (state) {
      case "blocked":
        return {
          title: t("liveRegie_title_blocked"),
          subtitle: t("liveRegie_sub_blocked"),
        };
      case "ready":
        return {
          title: t("liveRegie_title_ready"),
          subtitle: t("liveRegie_sub_ready"),
        };
      case "scheduled":
        return {
          title: t("liveRegie_title_scheduled"),
          subtitle: t("liveRegie_sub_scheduled", {
            time: formatReadyTime(view.scheduledStart) ?? "",
          }),
        };
      case "provisioning":
        return {
          title: t("liveRegie_title_provisioning"),
          subtitle: t("liveRegie_sub_provisioning"),
        };
      case "provisioningFailed":
        return {
          title: t("liveRegie_title_provisioningFailed"),
          subtitle: t("liveRegie_sub_provisioningFailed"),
        };
      case "waitingSignal":
        return view.obsDisconnected
          ? {
              title: t("liveRegie_signalLostTitle"),
              subtitle: t("liveRegie_sub_obsDisconnected"),
            }
          : {
              title: t("liveRegie_title_waitingSignal"),
              subtitle: t("liveRegie_sub_waitingSignal"),
            };
      case "checkingSignal":
        return {
          title: t("liveRegie_title_checkingSignal"),
          subtitle: t("liveRegie_sub_checkingSignal"),
        };
      case "readyToAir":
      case "publishing":
        return {
          title: t("liveRegie_title_readyToAir"),
          subtitle: t("liveRegie_sub_readyToAir"),
        };
      case "live":
      case "degraded":
        return {
          title: endTime
            ? t("liveRegie_title_live", { time: endTime })
            : t("liveRegie_title_liveNoEnd"),
          subtitle:
            remainingMinutes === undefined
              ? ""
              : t("liveRegie_sub_live", { minutes: remainingMinutes }),
        };
      case "signalLost":
        return {
          title: t("liveRegie_signalLostTitle"),
          subtitle: endTime
            ? t("liveRegie_sub_signalLost", { time: endTime })
            : t("liveRegie_sub_signalLostNoEnd"),
        };
      case "stopping":
        return {
          title: t("liveRegie_title_stopping"),
          subtitle: t("liveRegie_sub_stopping"),
        };
      default:
        return {
          title: endTime
            ? t(
                state === "replayCreated"
                  ? "liveRegie_title_stoppedCreated"
                  : "liveRegie_title_stopped",
                { time: endTime },
              )
            : t("liveRegie_title_stoppedNoTime"),
          subtitle: "",
        };
    }
  }

  const { title, subtitle } = getTexts();

  function renderTerminatedSubtitle(): JSX.Element | null {
    if (phase !== "termine" || state === "stopping" || isArchived) return null;
    let reason: string | null = null;
    if (view.stopReason !== null) {
      reason = t(
        view.stopReason === "manual"
          ? "liveRegie_endedManual"
          : "liveRegie_endedScheduled",
      );
    }

    return (
      <p className="text-muted-foreground text-sm">
        {reason ? <span>{reason}</span> : null}
        {reason ? " · " : null}
        <span>
          {t("liveRegie_recordedDuration", {
            duration: formatRecordedDuration(settings.durationSeconds),
          })}
        </span>
        {state === "replayAvailable" ? (
          <span> {t("liveRegie_sub_replayAvailable")}</span>
        ) : null}
        {state === "replayCreated" ? (
          <span> {t("liveRegie_sub_replayCreated")}</span>
        ) : null}
      </p>
    );
  }

  function renderTroubleshootingLink(): JSX.Element {
    return (
      <Button asChild variant="outline">
        <a href={troubleshootingUrl} rel="noreferrer" target="_blank">
          {t("liveRegie_troubleshooting")}
        </a>
      </Button>
    );
  }

  function renderStopAction(): JSX.Element {
    return (
      <LiveConfirmAction
        confirmLabel={t("liveRegie_stopConfirm")}
        description={t("liveRegie_stopConfirmDescription")}
        disabled={Boolean(isMutating.stop)}
        label={t("liveRegie_stop")}
        onConfirm={actions.stop}
        title={t("liveRegie_stopConfirmTitle")}
        variant="destructive"
      />
    );
  }

  function renderActions(): JSX.Element | null {
    if (isArchived) return null;

    switch (state) {
      case "blocked":
      case "ready":
      case "scheduled":
        return (
          <Button
            disabled={state === "blocked" || Boolean(isMutating.start)}
            onClick={actions.start}
          >
            {t(
              state === "scheduled"
                ? "liveRegie_getStreamCredentialsNow"
                : "liveRegie_getStreamCredentials",
            )}
          </Button>
        );
      case "provisioningFailed":
        return (
          <Button
            disabled={Boolean(isMutating.retryProvisioning)}
            onClick={actions.retryProvisioning}
          >
            {t("liveRegie_retry")}
          </Button>
        );
      case "provisioning":
        return null;
      case "waitingSignal":
      case "checkingSignal":
      case "readyToAir":
      case "publishing":
        return (
          <div className="flex flex-col items-end gap-1">
            <Button disabled={state !== "readyToAir"} onClick={actions.publish}>
              {state === "publishing" ? (
                <Loader2 aria-hidden="true" className="animate-spin" />
              ) : null}
              {t("liveRegie_goOnAir")}
            </Button>
            {state !== "readyToAir" && state !== "publishing" ? (
              <span className="text-muted-foreground text-xs">
                {t("liveRegie_availableOnSignal")}
              </span>
            ) : null}
          </div>
        );
      case "live":
      case "degraded":
        return (
          <React.Fragment>
            <LiveConfirmAction
              confirmLabel={t("liveRegie_extendConfirm")}
              description={t("liveRegie_extendConfirmDescription")}
              disabled={!regie.canExtend || Boolean(isMutating.extend)}
              label={t("liveRegie_extend")}
              onConfirm={actions.extend}
              title={t("liveRegie_extendConfirmTitle")}
              variant="outline"
              tooltip={
                regie.canExtend ? undefined : t("liveRegie_extendQuotaTooltip")
              }
            />
            {renderStopAction()}
          </React.Fragment>
        );
      case "signalLost":
        return (
          <React.Fragment>
            {renderTroubleshootingLink()}
            {renderStopAction()}
          </React.Fragment>
        );
      case "replayAvailable":
        return (
          <LiveConfirmAction
            confirmLabel={t("liveRegie_createReplayConfirm")}
            disabled={Boolean(isMutating.createReplay)}
            label={t("liveRegie_createReplay")}
            onConfirm={() => actions.createReplay(range)}
            title={t("liveRegie_createReplayConfirmTitle")}
            description={t("liveRegie_replayConfirmDescription", {
              start: formatClock(range.start),
              end: formatClock(range.end),
              duration: formatRecordedDuration(range.end - range.start),
            })}
          />
        );
      case "replayCreated":
        return view.replayUuid ? (
          <Button asChild>
            <Link to={`/contents/hosted/${view.replayUuid}`}>
              {t("liveRegie_openReplay")}
            </Link>
          </Button>
        ) : null;
      default:
        return null;
    }
  }

  function renderSuggestion(suggestion: LiveQuotaSuggestion): JSX.Element {
    const isQuality = suggestion.kind === "quality";
    return (
      <Button
        key={`${suggestion.kind}-${suggestion.quality ?? suggestion.durationMinutes}`}
        size="sm"
        variant="outline"
        onClick={() =>
          actions.updateSettings(
            isQuality
              ? { maxResolution: suggestion.quality }
              : { durationSeconds: (suggestion.durationMinutes ?? 0) * 60 },
          )
        }
      >
        {isQuality
          ? t("liveRegie_suggestQuality", {
              quality: suggestion.quality,
              quota: formatQuotaDuration(suggestion.neededSeconds),
            })
          : t("liveRegie_suggestDuration", {
              minutes: suggestion.durationMinutes,
            })}
      </Button>
    );
  }

  function renderBlockers(): JSX.Element | null {
    if (state !== "blocked" || view.blockers.length === 0) return null;

    return (
      <ul className="flex flex-col gap-2 border-t pt-4 text-sm">
        {view.blockers.includes("unsaved") ? (
          <li className="flex flex-wrap items-center justify-between gap-2">
            <span>{t("liveRegie_blockerUnsaved")}</span>
            <Button onClick={actions.save} size="sm" variant="outline">
              {t("liveRegie_save")}
            </Button>
          </li>
        ) : null}
        {view.blockers.includes("quota") ? (
          <li className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {t("liveRegie_blockerQuota", {
                needed: formatQuotaDuration(regie.quota?.neededSeconds ?? 0),
                available: formatQuotaDuration(
                  regie.quota?.availableBeforeSeconds ?? 0,
                ),
              })}
            </span>
            <span className="flex flex-wrap gap-2">
              {regie.suggestions.map(renderSuggestion)}
            </span>
          </li>
        ) : null}
        {view.blockers.includes("settings") ? (
          <li>{t("liveRegie_blockerSettings")}</li>
        ) : null}
      </ul>
    );
  }

  function renderAlerts(): JSX.Element | null {
    if (isArchived) return null;
    const { quality } = view;
    return (
      <React.Fragment>
        {state === "degraded" ? (
          <Alert variant={quality === "outage" ? "destructive" : "warning"}>
            <AlertTitle>
              {t(
                quality === "outage"
                  ? "liveRegie_alert_outageTitle"
                  : "liveRegie_alert_degradedTitle",
              )}
            </AlertTitle>
            <AlertDescription>
              <p>{t("liveRegie_alert_degradedDescription")}</p>
              <a
                className="underline"
                href={troubleshootingUrl}
                rel="noreferrer"
                target="_blank"
              >
                {t("liveRegie_troubleshootingShort")}
              </a>
            </AlertDescription>
          </Alert>
        ) : null}
        {regie.extendQuotaRefused ? (
          <Alert variant="destructive">
            <AlertDescription>
              {t("liveRegie_extendQuotaRefused")}
            </AlertDescription>
          </Alert>
        ) : null}
      </React.Fragment>
    );
  }

  const actionsNode = renderActions();

  return (
    <Card
      className={cn(
        "gap-4 py-4",
        !isArchived && state === "provisioningFailed" && "border-status-error",
      )}
    >
      <CardContent className="flex flex-col gap-4 px-4">
        <div className="flex flex-wrap items-center gap-4">
          <span
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-full",
              TONE_ICON_CLASS[style.tone],
            )}
          >
            <style.Icon
              aria-hidden="true"
              className={cn("size-5", isSpinning && "animate-spin")}
            />
          </span>
          <div className="min-w-0 flex-1 basis-64">
            <h2 className="text-base font-semibold">{title}</h2>
            {subtitle ? (
              <p className="text-muted-foreground text-sm">{subtitle}</p>
            ) : null}
            {renderTerminatedSubtitle()}
          </div>
          {actionsNode ? (
            <div className="flex flex-wrap items-center gap-2">
              {actionsNode}
            </div>
          ) : null}
        </div>
        {renderBlockers()}
        {renderAlerts()}
        <LivePhaseGauge
          phase={phase}
          progress={progress}
          remainingMinutes={remainingMinutes}
          state={state}
        />
        {regie.isStale ? (
          <p className="text-muted-foreground text-xs">
            {t("liveRegie_staleData")}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default LiveStatusBar;
